// ** Type import

import { useGrowthBook } from '@/src/context/GrowthBookProvider'
import { FeatureFlag } from '@/src/types/common'
import { VerticalNavItemsType } from 'src/@core/layouts/types'
import { useAuth } from '@/src/hooks/useAuth'

// ** Navigation sections imports
const Navigation = (growthbook?: any, auth?: any): VerticalNavItemsType => {
  // Always call hooks first, then use provided values if available
  const hookGrowthbook = useGrowthBook()
  const hookAuth = useAuth()

  const growthbookData = growthbook || hookGrowthbook
  const authData = auth || hookAuth

  const isMasterProductListEnabled = growthbookData?.isOn(FeatureFlag.MASTER_PRODUCT_LIST) || false
  const isFranchiseModuleEnabled = growthbookData?.isOn(FeatureFlag.FRANCHISE_HUB) || false
  const isOrderManagementEnabled = growthbookData?.isOn(FeatureFlag.ORDER_MANAGEMENT) || false
  const isRecipeGuideEnabled = growthbookData?.getFeatureValue(FeatureFlag.RECIPE_MANAGEMENT, false) || false
  const isKitchenManagementEnabled = growthbookData?.isOn(FeatureFlag.KITCHEN_MANAGEMENT) || false
  const isRestaurantInsightsEnabled = growthbookData?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false

  return [
    {
      title: 'Restaurant',
      icon: 'mynaui:store',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: 'Restaurant List',
          path: '/app/restaurant',
          icon: 'mynaui:store',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: 'Allocation & Delegation',
          path: '/app/restaurant/allocation',
          icon: 'mdi:account-multiple',
          subject: 'restaurant-allocation',
          action: 'read',

          // Custom check: requires both restaurant-allocation and restaurant-delegation read permissions
          customPermissionCheck: (user: any) => {
            const hasAllocationRead = user?.permissions?.['restaurant-allocation']?.includes('read') || false
            const hasDelegationRead = user?.permissions?.['restaurant-delegation']?.includes('read') || false

            return hasAllocationRead && hasDelegationRead
          }
        },
        {
          title: 'Reporting',
          path: '/app/submissions',
          icon: 'mynaui:store',
          subject: 'restaurant',
          action: 'read'
        },

        // {
        //   title: 'Operational Changes',
        //   path: '/app/history-logs',
        //   icon: 'bxs:historylogs',
        //   subject: 'restaurant',
        //   action: 'read'
        // },

        {
          title: 'Disabled Ingredients',
          icon: 'bxs:disabled-ingredients',
          subject: 'restaurant',
          action: 'read',
          children: [
            {
              title: 'List',
              path: '/app/disabled-ingredients',
              icon: 'bxs:disabled-ingredients',
              subject: 'restaurant',
              action: 'read'
            },
            {
              title: 'Reports',
              path: '/app/disabled-ingredients/reports',
              icon: 'mdi:file-document-multiple-outline',
              subject: 'restaurant',
              action: 'read'
            }
          ]
        },
        {
          title: 'Reports',
          icon: 'bxs:disabled-ingredients',
          subject: 'restaurant',
          action: 'read',
          children: [
            {
              title: 'Weekly Audit Logs',
              path: '/app/audit-logs/reports',
              icon: 'mdi:file-document-multiple-outline',
              subject: 'restaurant',
              action: 'read'
            }
          ]
        },
        {
          title: 'Store Surcharges',
          path: '/app/marketing-portal/store-surcharges',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: 'Menu Jobs',
          path: '/app/incomplete-menu-jobs',
          icon: 'bxs:menu',
          subject: 'job-management',
          action: 'read'
        }
      ]
    },
    ...(isRestaurantInsightsEnabled
      ? [
          {
            title: 'GYG Oracle',
            icon: 'tabler:device-analytics',
            subject: 'restaurant',
            action: 'read',
            children: [
              {
                title: 'Restaurant Insights',
                path: '/app/restaurant-insights',
                icon: 'tabler:chart-line',
                subject: 'restaurant',
                action: 'read'
              },
              {
                title: 'AI Insights',
                path: '/app/ai-insights',
                icon: 'tabler:robot',
                subject: 'restaurant',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    ...(isFranchiseModuleEnabled
      ? [
          {
            title: 'Franchise Hub',
            icon: 'mdi:account-group',
            subject: 'franchise-hub',
            action: 'read',
            children: [
              {
                title: 'Franchise List',
                path: '/app/franchise-hub',
                icon: 'mdi:account-group',
                subject: 'franchise-hub',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    {
      title: 'Customer Support',
      icon: 'ant-design:customer-service-outlined',
      path: '/app/customer-portal',
      subject: 'guest-experience',
      action: 'read'
    },
    ...(isOrderManagementEnabled
      ? [
          {
            title: 'Order Management',
            icon: 'mynaui:cart',
            path: '/app/order-management',
            subject: 'order-management',
            action: 'read'
          }
        ]
      : []),
    ...(isMasterProductListEnabled
      ? [
          {
            title: 'Products',
            path: '/app/products/mainmenu/mains/5',
            icon: 'material-symbols:contract-outline',
            subject: 'product',
            action: 'read'
          }
        ]
      : []),

    ...(isRecipeGuideEnabled
      ? [
          {
            title: 'Recipes',
            icon: 'mdi:book-open-outline',
            subject: 'recipe',
            action: 'read',
            children: [
              {
                title: 'All Recipes',
                path: '/app/recipe-mgmt',
                icon: 'mdi:account-group',
                subject: 'recipe',
                action: 'read'
              },
              {
                title: 'Equipments',
                path: '/app/recipe-guide-equipments',
                icon: 'mdi:account-group',
                subject: 'recipe-guide-equipment',
                action: 'read'
              },
              {
                title: 'Hazard Tags',
                path: '/app/food-safety-tags',
                icon: 'mdi:tag',
                subject: 'recipe',
                action: 'read'
              },
              {
                title: 'Ingredients',
                path: '/app/recipe-guide-ingredients',
                icon: 'mdi:account-group',
                subject: 'recipe',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    {
      title: 'Marketing',
      icon: 'pixelarticons:chart',
      subject: 'marketing',
      action: 'read',
      children: [
        {
          title: 'Offers',
          path: '/app/marketing-portal/offers',
          icon: 'bxs:offer',
          subject: 'marketing',
          action: 'read'
        },
        {
          title: 'Nutritional Info',
          path: '/app/marketing-portal/nutritional-info',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        },
        {
          title: 'Nutritional Used Quantity',
          path: '/app/marketing-portal/nutritional-info/used-quantity',
          subject: 'ingredient-nutritional-info',
          action: 'read'
        }
      ]
    },
    {
      title: 'Users',
      icon: 'tabler:users',
      path: '/app/user-management',
      subject: 'user-management',
      action: 'read',
      children: [
        {
          title: 'User Role',
          path: '/app/user-management',
          icon: 'bxs:offer',
          subject: 'user-management',
          action: 'manage'
        },

        // {
        //   title: 'Role Delegation',
        //   path: '/app/role-delegation',
        //   icon: 'mdi:account-switch',
        //   subject: 'user-management',
        //   action: 'manage'
        // },
        {
          title: 'User List',
          path: '/app/user-management/list',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'User Profile',
          path: authData.user
            ? `/app/user-management/profile/?userId=${authData.user.id}`
            : '/app/user-management/profile',
          icon: 'mdi:account-circle',
          subject: 'user-management',
          action: 'read'
        },
        {
          title: 'History Logs',
          path: '/app/user-management/changes',
          icon: 'mingcute:inventory-line',
          subject: 'user-management',
          action: 'read'
        }
      ]
    },
    {
      title: `FAQ's List`,
      icon: 'ic:outline-quiz',
      path: '/app/faq',
      subject: 'faq',
      action: 'read'
    },
    {
      title: 'Admin Tools',
      icon: 'tabler:tools',
      children: [
        {
          title: 'Announcements',
          path: '/app/announcements',
          icon: 'tabler:megaphone',
          subject: 'announcements',
          action: 'manage'
        },
        {
          title: 'Menu Equipments',
          path: '/app/menu-equipments',
          icon: 'mdi:chef-hat',
          subject: 'menu-equipment',
          action: 'manage'
        },
        {
          title: 'Ingredients',
          path: '/app/admin-tools-ingredients',
          icon: 'mdi:food-variant',
          subject: 'menu',
          action: 'update'
        }
      ]
    },
    ...(isKitchenManagementEnabled
      ? [
          {
            title: 'Kitchen Management',
            icon: 'mdi:chef-hat',
            subject: 'kitchen-management',
            action: 'read',
            children: [
              {
                title: 'OMS Restaurants',
                path: '/app/kitchen-management/oms-restaurants',
                icon: 'mdi:store',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Apps',
                path: '/app/kitchen-management/oms-apps',
                icon: 'mdi:application-outline',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Products',
                path: '/app/kitchen-management/oms-products',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Bundles',
                path: '/app/kitchen-management/oms-bundles',
                icon: 'mdi:food',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Settings',
                path: '/app/kitchen-management/oms-settings',
                icon: 'mdi:cog',
                subject: 'kitchen-management',
                action: 'read'
              }
            ]
          }
        ]
      : [])
  ]
}

// ** Navigation sections imports
export const SubNavigation = (): VerticalNavItemsType => {
  const growthbook = useGrowthBook()
  const isRssFeatureEnabled = growthbook?.isOn(FeatureFlag.RSS) || false
  const isKitchenManagementEnabled = growthbook?.isOn(FeatureFlag.KITCHEN_MANAGEMENT) || false
  const isRestaurantInsightsEnabled = growthbook?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false

  return [
    {
      title: 'Restaurant Details',
      path: '/app/restaurant/[id]',
      subject: 'restaurant',
      action: 'read'
    },
    {
      title: 'Menu & Services',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: 'Menu Management',
          subject: 'menu',
          path: '/app/restaurant/[id]/menu-services/menu-management',
          action: 'read'
        },
        {
          title: 'Trading Hours',
          subject: 'restaurant',
          path: '/app/restaurant/[id]/menu-services/trading-hours',
          action: 'read'
        },
        {
          title: 'Order offset',
          subject: 'restaurant',
          path: '/app/restaurant/[id]/menu-services/order-offset',
          action: 'read'
        },
        {
          title: 'Equipment',
          path: '/app/restaurant/[id]/menu-services/equipment',
          subject: 'equipment',
          action: 'read'
        },
        {
          title: 'History Log',
          path: '/app/restaurant/[id]/history-log',
          subject: 'restaurant',
          action: 'read'
        }
      ]
    },
    {
      title: 'Operations',
      subject: 'restaurant',
      path: '/app/restaurant/[id]/operations',
      action: 'read',
      children: [
        {
          title: 'Ready to Roll?',
          path: '/app/restaurant/[id]/ready-to-roll/add',
          subject: 'operations-checklist',
          action: 'read'
        },
        {
          title: 'End of Night',
          path: '/app/restaurant/[id]/end-of-night',
          subject: 'operations-checklist',
          action: 'read'
        },
        ...(isRssFeatureEnabled
          ? [
              {
                title: 'Cleaning Schedule',
                path: '/app/restaurant/[id]/rss/add',
                subject: 'rss',
                action: 'read'
              }
            ]
          : [])
      ]
    },
    ...(isKitchenManagementEnabled
      ? [
          {
            title: 'Kitchen Management',
            icon: 'mdi:chef-hat',
            subject: 'kitchen-management',
            action: 'read',
            children: [
              {
                title: 'OMS Lines',
                path: '/app/restaurant/[id]/kitchen-management/oms-lines',
                icon: 'mdi:view-list',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Printers',
                path: '/app/restaurant/[id]/kitchen-management/oms-printers',
                icon: 'mdi:printer',
                subject: 'kitchen-management',
                action: 'read'
              },
              {
                title: 'OMS Order Types',
                path: '/app/restaurant/[id]/kitchen-management/oms-order-types',
                icon: 'mdi:clipboard-list',
                subject: 'kitchen-management',
                action: 'read'
              }
            ]
          }
        ]
      : []),
    {
      title: 'Reporting',
      subject: 'operations-checklist',
      path: '/app/restaurant/[id]/reporting',
      action: 'read',
      children: [
        ...(isRestaurantInsightsEnabled
          ? [
              {
                title: 'Ready to Roll?',
                path: '/app/restaurant/[id]/report-ready-to-roll',
                subject: 'operations-checklist',
                action: 'read'
              }
            ]
          : []),
        ...(isRssFeatureEnabled
          ? [
              {
                title: 'Cleaning Schedule',
                path: '/app/restaurant/[id]/report-rss',
                subject: 'rss',
                action: 'read'
              }
            ]
          : [])
      ]
    },
    {
      title: `Calendar`,
      path: '/app/restaurant/[id]/calendar',
      subject: 'restaurant',
      action: 'read'
    }
  ]
}

// Mobile navigation
export const SubNavigationMobile = (): VerticalNavItemsType => {
  const growthbook = useGrowthBook()
  const isRssFeatureEnabled = growthbook?.isOn(FeatureFlag.RSS) || false

  return [
    {
      title: 'Restaurant Details',
      path: '/app/restaurant/[id]',
      subject: 'restaurant',
      action: 'read'
    },
    {
      title: 'Menu & Services',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: 'Menu Management',
          subject: 'menu',
          path: '/app/restaurant/[id]/menu-services/menu-management',
          action: 'read'
        },
        {
          title: 'Trading Hours',
          subject: 'restaurant',
          path: '/app/restaurant/[id]/menu-services/trading-hours',
          action: 'read'
        },
        {
          title: 'Order offset',
          subject: 'restaurant',
          path: '/app/restaurant/[id]/menu-services/order-offset',
          action: 'read'
        },
        {
          title: 'Equipment',
          path: '/app/restaurant/[id]/menu-services/equipment',
          subject: 'equipment',
          action: 'read'
        }
      ]
    },
    {
      title: 'Operations',
      subject: 'restaurant',
      action: 'read',
      children: [
        {
          title: 'History Log',
          path: '/app/restaurant/[id]/history-log',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: `Calendar`,
          path: '/app/restaurant/[id]/calendar',
          subject: 'restaurant',
          action: 'read'
        },
        {
          title: 'Ready to Roll?',
          path: '/app/restaurant/[id]/ready-to-roll/add',
          subject: 'operations-checklist',
          action: 'read'
        },
        {
          title: 'End of Night',
          path: '/app/restaurant/[id]/end-of-night',
          subject: 'operations-checklist',
          action: 'read'
        },
        ...(isRssFeatureEnabled
          ? [
              {
                title: 'Cleaning Schedule',
                path: '/app/restaurant/[id]/rss/add',
                subject: 'rss',
                action: 'read'
              }
            ]
          : [])
      ]
    }
  ]
}

// Restaurant user navigation
export const SubNavigationRestaurant = (): VerticalNavItemsType => {
  const growthbook = useGrowthBook()
  const isRssFeatureEnabled = growthbook?.isOn(FeatureFlag.RSS) || false
  const isRestaurantInsightsEnabled = growthbook?.isOn(FeatureFlag.RESTAURANT_INSIGHTS) || false

  return [
    {
      title: 'Restaurant Details',
      path: '/app/restaurant/[id]',
      subject: 'restaurant',
      action: 'read'
    },
    {
      title: 'Operations',
      subject: 'restaurant',
      path: '/app/restaurant/[id]/operations',
      action: 'read',
      children: [
        {
          title: 'Ready to Roll?',
          path: '/app/restaurant/[id]/ready-to-roll/add',
          subject: 'operations-checklist',
          action: 'read'
        },
        {
          title: 'End of Night',
          path: '/app/restaurant/[id]/end-of-night',
          subject: 'operations-checklist',
          action: 'read'
        },
        ...(isRssFeatureEnabled
          ? [
              {
                title: 'Cleaning Schedule',
                path: '/app/restaurant/[id]/rss/add',
                subject: 'rss',
                action: 'read'
              }
            ]
          : [])
      ]
    },
    {
      title: 'Reporting',
      subject: 'operations-checklist',
      path: '/app/restaurant/[id]/reporting',
      action: 'read',
      children: [
        ...(isRestaurantInsightsEnabled
          ? [
              {
                title: 'Ready to Roll?',
                path: '/app/restaurant/[id]/report-ready-to-roll',
                subject: 'operations-checklist',
                action: 'read'
              }
            ]
          : []),
        ...(isRssFeatureEnabled
          ? [
              {
                title: 'Cleaning Schedule',
                path: '/app/restaurant/[id]/report-rss',
                subject: 'rss',
                action: 'read'
              }
            ]
          : [])
      ]
    }
  ]
}

export const ProductSubNavigation = (): VerticalNavItemsType => {
  const growthbook = useGrowthBook()
  const isMasterProductListEnabled = growthbook?.isOn(FeatureFlag.MASTER_PRODUCT_LIST) || false

  if (!isMasterProductListEnabled) {
    return []
  }

  return [
    {
      title: 'Breakfast',
      path: '/app/products/breakfast',
      subject: 'product',
      action: 'read'
    },
    {
      title: 'Main-Menu',
      subject: 'product',
      action: 'read',
      children: [
        {
          title: 'Mains',
          path: '/app/products/mainmenu/mains/5',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Minis',
          path: '/app/products/mainmenu/minis',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Sides',
          path: '/app/products/mainmenu/sides',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Cold Drinks',
          path: '/app/products/mainmenu/cold-drinks',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Desserts',
          path: '/app/products/mainmenu/desserts',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Bundles',
          path: '/app/products/mainmenu/bundles',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Kids',
          path: '/app/products/mainmenu/kids',
          subject: 'product',
          action: 'read'
        },
        {
          title: 'Café Hola!',
          path: '/app/products/mainmenu/cafe-hola',
          subject: 'product',
          action: 'read'
        }
      ]
    },
    {
      title: 'Catering Breakfast',
      path: '/app/products/catering-breakfast',
      subject: 'product',
      action: 'read'
    },
    {
      title: 'Catering Lunch',
      path: '/app/products/catering-lunch',
      subject: 'product',
      action: 'read'
    },
    {
      title: 'Catering Dinner',
      path: '/app/products/catering-dinner',
      subject: 'product',
      action: 'read'
    },
    {
      title: 'Uncategorised',
      path: '/app/products/uncategorised',
      subject: 'product',
      action: 'read'
    },
    {
      title: 'Management',
      path: '/app/products/management',
      subject: 'product',
      action: 'read'
    }
  ]
}

// Franchise user navigation
export const FranchiseSubNavigation = (): VerticalNavItemsType => {
  const growthbook = useGrowthBook()
  const isFranchiseModuleEnabled = growthbook?.isOn(FeatureFlag.FRANCHISE_HUB) || false

  return isFranchiseModuleEnabled
    ? [
        {
          title: 'Franchise Reviews',
          path: '/app/franchise-hub/[id]',
          subject: 'franchise-hub',
          action: 'read'
        }
      ]
    : []
}

export default Navigation
